import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  VIDEO_UPLOAD_CM_RESET,
  VIDEO_UPLOAD_RESET,
  VIDEO_UPLOAD_START_ONLINE_RESET,
} from "../../../redux/actions/types";
import {
  first_name,
  isAdminOrCM,
  join_room_role,
  loan_details,
  video_pdn_id,
  req_user_id,
} from "../../../utils";
import {
  useMediaPermission,
  useAddLog,
  useCloseMeeting,
  useEndCall,
  useAnalyze,
  useAudio,
  useVideo,
  useJoinMeeting,
  useCaptureFrame,
  useStartRecording,
  useStartRecordingCm,
  useStopRecordingAndUpload,
  useStopRecordingAndUploadCm,
  useStartCall,
} from "../../../hooks/F2FVideoConferencing";
import { useScreenRecording } from "../../../hooks/F2FVideoConferencing/useScreenRecording";
import F2FVideoUIPage from "./F2FVideoUIPage";
import VideoUploadAction from "../../../redux/actions/videoPD/VideoUploadAction";
import VideoUploadCmAction from "../../../redux/actions/videoPD/VIdeoUploadCmAction";

const F2FVideoPage = () => {
  const { id } = useParams();
  const meetingId = id;
  const localRef = React.useRef(null);
  const remoteRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const pcRef = React.useRef(null);
  const firstName = first_name();
  const frameIndexRef = React.useRef(0);
  const [connected, setConnected] = React.useState(false);
  const [remoteVideoActive, setRemoteVideoActive] = React.useState(false);
  const [responses, setResponses] = React.useState([]);
  const [hasJoined, setHasJoined] = React.useState(false);
  const [hasAnalysisStarted, setHasAnalysisStarted] = React.useState(false);
  const [connectionId, setConnectionId] = React.useState(null);
  const [isRemoteVideoOn, setIsRemoteVideoOn] = React.useState(true);
  const [isRemoteUserInRoom, setIsRemoteUserInRoom] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isUploadingCm, setIsUploadingCm] = React.useState(false);
  const [isRecording, setIsRecording] = React.useState(false);
  const [isRecordingCm, setIsRecordingCm] = React.useState(false); // Separate recording state for CM
  // use Ref to track elapsed time to avoid re-renders of the whole page every second
  const elapsedTimeRef = React.useRef(0);
  const [isMeetingEnded, setIsMeetingEnded] = React.useState(false);
  const [callStarted, setCallStarted] = React.useState(false);
  // Screen recording hook (admin only)
  const { isScreenRecording, handleScreenRecording } = useScreenRecording();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Redux state listener for upload success
  const videoUploadData = useSelector((state) => state.videoUploadReducer);
  // Video Upload CM Reducer
  const videoUploadCmData = useSelector((state) => state.videoUploadCmReducer);

  const location = useLocation();
  const mediaRecorderRef = React.useRef(null);
  const cmMediaRecorderRef = React.useRef(null); // Separate media recorder for CM
  const chunksRef = React.useRef([]);
  const cmChunksRef = React.useRef([]); // Separate chunks for CM
  const timerRef = React.useRef(null);

  // Custom hook which manage add/remove logs below video conferencing
  const { handleLogs, logs, setLogs } = useAddLog();

  // Custom hook which manage media permission hook
  const { permission, handleRequestMedia, setPermission } = useMediaPermission(
    localRef,
    pcRef,
    handleLogs,
  );

  // Custom hook which manage close meeting
  const { handleCloseMeeting } = useCloseMeeting(
    isAdminOrCM,
    wsRef,
    handleLogs,
  );

  // Custom hook which manage video conferencing audio
  const { toggleAudio, isAudioOn, setIsAudioOn } = useAudio(
    localRef,
    handleLogs,
  );

  // Custom hook which manage video conferencing video
  const { toggleVideo, isVideoOn, setIsVideoOn } = useVideo(
    localRef,
    handleLogs,
    wsRef,
  );

  // Custom hook which manage join meeting
  const { handleJoinMeeting } = useJoinMeeting(
    wsRef,
    handleLogs,
    connected,
    firstName,
    join_room_role,
    handleRequestMedia,
    permission,
    isAdminOrCM,
  );

  // Custom hook which manage capture frame of image
  const { captureFrame } = useCaptureFrame(remoteRef);

  // Custom hook which manage upload_video_score_process API
  const { stopRecordingAndUpload } = useStopRecordingAndUpload(
    mediaRecorderRef,
    timerRef,
    setIsRecording,
    setIsUploading,
    chunksRef,
    loan_details,
    video_pdn_id,
    req_user_id,
    frameIndexRef,
    elapsedTimeRef,
    dispatch,
    VideoUploadAction,
    meetingId,
  );

  // Custom hook which manage video conferencing analyze (Applicant - uses remote video)
  const {
    handleAnalyzeToggle: handleAnalyzeToggleApplicant,
    isAnalyzing: isAnalyzingApplicant,
  } = useAnalyze(
    remoteRef,
    meetingId,
    frameIndexRef,
    remoteVideoActive,
    handleLogs,
    setResponses,
    setHasAnalysisStarted,
    video_pdn_id,
    "Applicant", // userType
  );

  // Analyze toggle is just for applicant - CM only uploads video file
  const handleAnalyzeToggle = handleAnalyzeToggleApplicant;

  // Analyzing state is just for applicant
  const isAnalyzing = isAnalyzingApplicant;

  // Custom hook which manage CM video upload (only .mp4 file, no frames)
  const { stopRecordingAndUploadCm } = useStopRecordingAndUploadCm(
    cmMediaRecorderRef,
    timerRef,
    setIsRecordingCm,
    setIsUploadingCm,
    cmChunksRef,
    loan_details,
    dispatch,
    VideoUploadCmAction,
  );

  // Custom hook which manage end call
  const { handleEndCall: originalHandleEndCall } = useEndCall(
    pcRef,
    wsRef,
    localRef,
    setConnected,
    setHasJoined,
    setConnectionId,
    setIsVideoOn,
    setIsAudioOn,
    setIsRemoteVideoOn,
    setIsRemoteUserInRoom,
    setPermission,
    setRemoteVideoActive,
    handleLogs,
    isAdminOrCM,
    isRecording,
    captureFrame,
    stopRecordingAndUpload,
    stopRecordingAndUploadCm,
  );

  // Custom hook which manage start recording of media
  const { startRecording } = useStartRecording(
    isRecording,
    remoteRef,
    mediaRecorderRef,
    chunksRef,
    setIsRecording,
    () => {}, // Do not reset elapsed time on start recording
    handleLogs,
  );

  // Custom hook which manage start recording of media for CM (from local video)
  const { startRecordingCm } = useStartRecordingCm(
    isRecordingCm,
    localRef,
    cmMediaRecorderRef,
    cmChunksRef,
    setIsRecordingCm,
    handleLogs,
  );

  // Wrapper for remote end call to trigger UI update for all users
  const handleRemoteEndCall = React.useCallback(
    (data) => {
      originalHandleEndCall(data);
      // Set meeting ended for all users (both Admin/CM and Participants)
      setIsMeetingEnded(true);
    },
    [originalHandleEndCall],
  );

  // Custom hook which manage start peer connection with different methods which use to share media, port, ip address
  const { handleStartCall } = useStartCall(
    handleLogs,
    setHasJoined,
    setIsRemoteUserInRoom,
    pcRef,
    wsRef,
    handleRemoteEndCall,
    setIsRemoteVideoOn,
    connectionId,
    setConnectionId,
    setConnected,
    meetingId,
    setRemoteVideoActive,
    remoteRef,
    localRef,
  );

  const handleClearLogs = React.useCallback(() => {
    setLogs([]);
  }, [setLogs]);

  /**
   * Wrapper for handleCloseMeeting to ensure Admin and Participants upload recording.
   * For Admin/CM: Send close_meeting and wait for server's meeting_closed response
   * For Participants: Just send close_meeting and end locally
   */
  const handleEndMeetingClick = React.useCallback(() => {
    // 1. Notify signaling room first (broadcast to all)
    handleCloseMeeting();

    if (!isAdminOrCM) {
      // For participants, end immediately
      setIsMeetingEnded(true);
      toast.success(
        "Meeting ended successfully. Thank you for joining meeting.",
        { autoClose: 2000, toastId: "meeting_ended_successfully" },
      );
      // Perform cleanup for participants
      originalHandleEndCall();
    } else {
      // For Admin/CM, wait for server's meeting_closed response
      handleLogs("Ending meeting and waiting for server response...");
      // The meeting_closed handler in useStartCall will call originalHandleEndCall
    }
  }, [handleCloseMeeting, handleLogs, originalHandleEndCall]);

  // Run on dependency change to check query params and remove user_type if not admin
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userTypeParam = searchParams.get("user_type");

    if (userTypeParam === "host" && !isAdminOrCM) {
      searchParams.delete("user_type");
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    }
  }, [location.search, location.pathname, navigate]);

  // Reset Redux state on component mount to prevent old data from triggering navigation
  React.useEffect(() => {
    dispatch({ type: VIDEO_UPLOAD_RESET });
    dispatch({ type: VIDEO_UPLOAD_CM_RESET });
    dispatch({ type: VIDEO_UPLOAD_START_ONLINE_RESET });
  }, [dispatch]);

  // Run on dependency change to start the meeting time on click of analyze button
  React.useEffect(() => {
    if (isAnalyzing) {
      timerRef.current = setInterval(() => {
        elapsedTimeRef.current += 1;
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isAnalyzing]);

  // Run on dependency change and navigate to history route when specific condition matches for video upload
  React.useEffect(() => {
    if (videoUploadData?.data?.status_code === 2000) {
      toast.success(videoUploadData?.data?.message, {
        autoClose: 2000,
        toastId: "video_uploaded_successfully",
      });
      navigate("/report/history");
      setIsUploading(false);
      localStorage.removeItem("loan_details");
      localStorage.removeItem("request_id");
      localStorage.removeItem("f2f_meeting_data");

      dispatch({ type: VIDEO_UPLOAD_START_ONLINE_RESET });
      dispatch({ type: VIDEO_UPLOAD_RESET });
      dispatch({ type: VIDEO_UPLOAD_CM_RESET });

      elapsedTimeRef.current = 0;

      // Close the parent window (F2FGenerateMeeting) if it exists
      if (window.opener && !window.opener.closed) {
        window.opener.close();
      }
    } else if (
      videoUploadData?.data?.status_code === 5000 ||
      videoUploadData?.data?.status_code === 4000
    ) {
      toast.error(videoUploadData?.data?.message, { autoClose: 2000 });
      dispatch({ type: VIDEO_UPLOAD_RESET });
      dispatch({ type: VIDEO_UPLOAD_CM_RESET });

      setIsUploading(false);
    }
  }, [videoUploadData, dispatch, navigate]);

  // Run on dependency change and navigate to history route when specific condition matches for video upload cm
  // React.useEffect(() => {
  //   if (videoUploadCmData?.data?.status_code === 2000) {
  //     toast.success(videoUploadCmData?.data?.message, {
  //       autoClose: 2000,
  //       toastId: "video_upload_cm_successfully",
  //     });
  //     navigate("/report/history");
  //     setIsUploadingCm(false);
  //     localStorage.removeItem("loan_details");
  //     localStorage.removeItem("request_id");
  //     localStorage.removeItem("f2f_meeting_data");

  //     dispatch({ type: VIDEO_UPLOAD_START_ONLINE_RESET });
  //     dispatch({ type: VIDEO_UPLOAD_CM_RESET });

  //     elapsedTimeRef.current = 0;

  //     // Close the parent window (F2FGenerateMeeting) if it exists
  //     if (window.opener && !window.opener.closed) {
  //       window.opener.close();
  //     }
  //   } else if (
  //     videoUploadCmData?.data?.status_code === 5000 ||
  //     videoUploadCmData?.data?.status_code === 4000
  //   ) {
  //     toast.error(videoUploadCmData?.data?.message, { autoClose: 2000 });
  //     dispatch({ type: VIDEO_UPLOAD_CM_RESET });
  //     setIsUploadingCm(false);
  //   }
  // }, [videoUploadCmData, dispatch, navigate]);

  // Run on dependency change when specific condition matches
  React.useEffect(() => {
    if (remoteVideoActive && !isRecording && !isUploading && !isUploadingCm) {
      startRecording();
      startRecordingCm(); // Also start CM recording
    }
  }, [
    remoteVideoActive,
    isRecording,
    isUploading,
    isUploadingCm,
    startRecording,
    startRecordingCm,
  ]);

  const handleStartTheCall = React.useCallback(() => {
    setCallStarted(true);
  }, []);

  return (
    <F2FVideoUIPage
      isAdminOrCM={isAdminOrCM}
      remoteRef={remoteRef}
      remoteVideoActive={remoteVideoActive}
      isRemoteVideoOn={isRemoteVideoOn}
      isRemoteUserInRoom={isRemoteUserInRoom}
      connected={connected}
      hasJoined={hasJoined}
      isAnalyzing={isAnalyzing}
      hasAnalysisStarted={hasAnalysisStarted}
      isVideoOn={isVideoOn}
      isAudioOn={isAudioOn}
      handleStartCall={handleStartCall}
      handleJoinMeeting={handleJoinMeeting}
      handleAnalyzeToggle={handleAnalyzeToggle}
      handleEndCall={originalHandleEndCall}
      handleCloseMeeting={handleEndMeetingClick}
      toggleVideo={toggleVideo}
      toggleAudio={toggleAudio}
      localRef={localRef}
      responses={responses}
      permission={permission}
      handleClearLogs={handleClearLogs}
      logs={logs}
      isUploading={isUploading || videoUploadData?.loading}
      isUploadingCm={isUploadingCm || videoUploadCmData?.loading}
      elapsedTimeRef={elapsedTimeRef}
      isMeetingEnded={isMeetingEnded}
      setIsMeetingEnded={setIsMeetingEnded}
      callStarted={callStarted}
      handleStartTheCall={handleStartTheCall}
      isScreenRecording={isScreenRecording}
      handleScreenRecording={handleScreenRecording}
    />
  );
};

export default F2FVideoPage;
